import { useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Col, Form, Input, Modal, Row } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  LogoutOutlined,
  MailOutlined,
  PhoneOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  ShoppingOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@core/hooks/useAuth';
import {
  useChangePassword,
  useProfile,
  useUpdateProfile,
  useDeleteAccount,
} from '@core/hooks/useAccount';
import { changePasswordSchema, profileSchema } from '@core/schemas/account';
import { useToast } from '@core/hooks/useToast';
import { getAvatarStyles } from '@core/utils/avatar';
import { useAddressBookStore } from '@core/store/addressBookStore';
import { useWishlistStore } from '@core/store/wishlistStore';
import { useGuestCartStore } from '@core/store/guestCartStore';
import Container from '@components/base/Container';
import Button from '@components/base/Button';
import LoadingState from '@components/base/LoadingState';
import MobileNumberField, { validateMobileNumber } from '@components/base/MobileNumberField';
import { applyZodErrors, applyApiFieldErrors } from '@features/auth/applyZodErrors.js';
import AppModal, { appModalClass } from '@components/base/AppModal';
import { useCopy } from '@/app/providers/useCopy';
import { useIndustry } from '@/app/providers/industry-context.js';
const TILES = [
  {
    to: '/orders',
    title: 'Orders',
    desc: 'View, track and cancel your past orders',
    icon: ShoppingOutlined,
    tone: 'warm',
  },
  {
    action: 'password',
    title: 'Password',
    desc: 'Update your password to keep your account safe',
    icon: LockOutlined,
    tone: 'green',
  },
];
export function useProfileDetails() {
  const profileQuery = useProfile();
  const { customer } = useAuth();
  const profile = profileQuery.data ?? {};
  const firstName = profile.first_name || customer?.first_name || '';
  const lastName = profile.last_name || customer?.last_name || '';
  const displayName =
    [firstName, lastName].filter(Boolean).join(' ') ||
    customer?.name ||
    customer?.email?.split('@')[0] ||
    '';
  const phoneNumber =
    profile.mobile?.number ||
    customer?.mobile?.number ||
    (typeof customer?.mobile === 'string' ? customer.mobile : '') ||
    customer?.phone ||
    '-';
  const mobile = profile.mobile || customer?.mobile;
  return { displayName, firstName, lastName, phoneNumber, mobile };
}
export default function AccountPage() {
  const { key: industry } = useIndustry();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const { customer, logout } = useAuth();
  const profileQuery = useProfile();
  const update = useUpdateProfile();
  const changePassword = useChangePassword();
  const deleteAccount = useDeleteAccount();
  const toast = useToast();
  const t = useCopy();
  const queryClient = useQueryClient();

  useEffect(() => {
    const data = profileQuery.data || customer;
    if (data) {
      form.setFieldsValue({
        // One "Full name" field, as in signup; split back into first/last on save.
        full_name: [data.first_name, data.last_name].filter(Boolean).join(' '),
        email: data.email ?? '',
        // The profile stores only calling_code + number (no ISO country), so
        // the country picker is resolved from calling_code. Without one,
        // MobileNumberField falls back to the store's country.
        mobile: {
          calling_code: data.mobile?.calling_code
            ? `+${String(data.mobile.calling_code).replace(/\D/g, '')}`
            : undefined,
          number: data.mobile?.number ?? '',
        },
      });
    }
  }, [form, profileQuery.data, customer]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isUploadMode, setIsUploadMode] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState(null);
  const uploadInputRef = useRef(null);

  const profile = profileQuery.data ?? {};
  const { displayName, firstName, phoneNumber } = useProfileDetails();
  const userFirstName = profile.first_name || customer?.first_name || firstName;
  const greetingName = userFirstName || displayName;
  const profilePhoto =
    selectedImagePreview || profile.avatar_url || profile.photo || profile.image || null;

  const openProfileFormEdit = () => {
    setIsUploadMode(false);
    setSelectedImageFile(null);
    setSelectedImagePreview(null);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedImageFile(null);
    setSelectedImagePreview(null);
  };

  const closePasswordModal = () => {
    setIsPasswordModalOpen(false);
    passwordForm.resetFields();
  };

  const openUpload = () => uploadInputRef.current?.click();

  const onImageSelected = (event) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) return;
    setSelectedImageFile(file);
    setSelectedImagePreview(URL.createObjectURL(file));
  };

  const onUploadSubmit = async () => {
    if (!selectedImageFile) return;
    const formData = new FormData();
    formData.append('photo', selectedImageFile);

    try {
      await update.mutateAsync(formData);
      toast.success(t('profileUpdated', 'Profile updated.'));
      closeEditModal();
    } catch (err) {
      applyApiFieldErrors(err, form);
      if (!err?.fields) {
        toast.apiError(err, t('couldNotUpdateProfile', 'Could not update profile.'));
      }
    }
  };

  useEffect(() => {
    return () => {
      if (selectedImagePreview) {
        URL.revokeObjectURL(selectedImagePreview);
      }
    };
  }, [selectedImagePreview]);

  const clearSessionAndRedirect = (msg) => {
    queryClient.clear();
    useAddressBookStore.getState().clear();
    useWishlistStore.getState().clear();
    useGuestCartStore.getState().clear();
    logout();

    const lang = localStorage.getItem('storefront-language');
    localStorage.clear();
    if (lang) {
      localStorage.setItem('storefront-language', lang);
    }

    sessionStorage.clear();
    if (msg) {
      sessionStorage.setItem('bytz.toast_message', msg);
    }
    window.location.replace('/' + window.location.search);
  };

  const onSignOut = () => {
    clearSessionAndRedirect(t('signedOut', 'Signed out.'));
  };

  const onDeleteAccount = () => {
    Modal.confirm({
      className: appModalClass('confirm'),
      title: t('deleteAccountConfirmTitle', 'Are you sure you want to delete your profile?'),
      okText: t('delete', 'Delete'),
      okType: 'danger',
      cancelText: t('cancel', 'Cancel'),
      centered: true,
      async onOk() {
        try {
          await deleteAccount.mutateAsync();
          clearSessionAndRedirect(t('accountDeletedSuccessfully', 'Profile deleted successfully.'));
        } catch (err) {
          toast.apiError(err, t('couldNotDeleteAccount', 'Could not delete profile.'));
        }
      },
    });
  };

  const onFinish = async (values) => {
    // The whole "Full name" goes in first_name. last_name is always cleared so
    // a surname saved at signup is not appended to it a second time.
    const { full_name: fullName, ...rest } = values;
    const payload = {
      ...rest,
      first_name: String(fullName || '')
        .trim()
        .replace(/\s+/g, ' '),
      last_name: '',
      mobile: {
        calling_code: values.mobile?.calling_code,
        number: values.mobile?.number ?? '',
      },
    };
    const parsed = profileSchema.safeParse(payload);
    if (!parsed.success) {
      applyZodErrors(parsed.error, form);
      return;
    }

    const requestData = selectedImageFile
      ? (() => {
          const formData = new FormData();
          Object.entries(parsed.data).forEach(([key, value]) => {
            if (value == null) return;
            formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
          });
          formData.append('photo', selectedImageFile);
          return formData;
        })()
      : parsed.data;

    try {
      await update.mutateAsync(requestData);
      toast.success(t('profileUpdated', 'Profile updated.'));
      closeEditModal();
      setSelectedImageFile(null);
      setSelectedImagePreview(null);
    } catch (err) {
      applyApiFieldErrors(err, form);
      if (!err?.fields) {
        toast.apiError(err, t('couldNotUpdateProfile', 'Could not update profile.'));
      }
    }
  };

  const onPasswordFinish = async (values) => {
    const parsed = changePasswordSchema.safeParse(values);
    if (!parsed.success) {
      applyZodErrors(parsed.error, passwordForm);
      return;
    }

    try {
      await changePassword.mutateAsync({
        currentPassword: parsed.data.currentPassword,
        newPassword: parsed.data.newPassword,
        confirmPassword: parsed.data.confirmPassword,
      });
      toast.success(t('passwordChanged', 'Password changed.'));
      closePasswordModal();
    } catch (err) {
      applyApiFieldErrors(err, passwordForm);
      if (!err?.fields) {
        toast.apiError(err, t('couldNotChangePassword', 'Could not change password.'));
      }
    }
  };

  if (profileQuery.isLoading) {
    return <LoadingState minHeight={320} />;
  }

  return (
    <Container size="lg" className="grocery-account-page">
      <div className="grocery-account-hero">
        <div>
          <h1>
            {t('hi', 'Hi')}, {greetingName} !
          </h1>
          <p>{t('accountSubtitle', 'Manage your profile, orders and account settings')}</p>
        </div>
        <div className="grocery-account-illustration" aria-hidden="true">
          <div className="grocery-account-leaf left" />
          <div className="grocery-account-leaf right" />
          <div className="grocery-account-id-card">
            <span className="grocery-account-id-avatar">
              <UserOutlined />
            </span>
            <span />
            <span />
            <span />
          </div>
          <div className="grocery-account-shield">
            <SafetyCertificateOutlined />
          </div>
        </div>
      </div>

      <div className="grocery-account-stack">
        {profileQuery.isError ? (
          <Alert
            type="error"
            showIcon
            message={
              profileQuery.error?.message ?? t('couldNotLoadProfile', 'Could not load profile.')
            }
          />
        ) : null}

        <section className="grocery-account-card">
          <div className="grocery-account-card-head">
            <h2>
              <UserOutlined />
              {t('accountInformation', 'Account information')}
            </h2>
            <div className="grocery-account-actions-wrap">
              <Button type="default" onClick={openProfileFormEdit} icon={<EditOutlined />}>
                {t('editProfile', 'Edit profile')}
              </Button>
              <Button
                type="default"
                danger
                onClick={onDeleteAccount}
                icon={<DeleteOutlined />}
                loading={deleteAccount.isPending}
                className="grocery-btn-delete-account"
              >
                {t('deleteProfile', 'Delete profile')}
              </Button>
            </div>
          </div>

          <div className="grocery-account-info-grid">
            <div className="grocery-account-identity">
              <span className="grocery-account-avatar-wrap">
                <Avatar
                  className="grocery-profile-avatar"
                  size={112}
                  src={profilePhoto}
                  style={getAvatarStyles(displayName)}
                >
                  {displayName[0]?.toUpperCase()}
                </Avatar>
              </span>

              <div className="grocery-account-contact">
                <h3>
                  {industry === 'grocery' || industry === 'meat'
                    ? userFirstName || displayName || '-'
                    : displayName}
                </h3>
                <p>
                  <MailOutlined />
                  <span>{profile.email || customer?.email || '-'}</span>
                </p>
                <p>
                  <PhoneOutlined />
                  <span>{phoneNumber}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        <Row gutter={[24, 24]}>
          {TILES.map((tile) => {
            const TileIcon = tile.icon;
            const tileContent = (
              <>
                <span className="grocery-account-tile-icon">
                  <TileIcon />
                </span>
                <span className="grocery-account-tile-copy">
                  <strong>{t(tile.title.toLowerCase(), tile.title)}</strong>
                  <small>{t(tile.title.toLowerCase() + 'Desc', tile.desc)}</small>
                </span>
                <span className="grocery-account-tile-action">
                  <RightOutlined />
                </span>
              </>
            );

            return (
              <Col key={tile.to ?? tile.action} xs={24} sm={12}>
                {tile.action === 'password' ? (
                  <button
                    type="button"
                    className={`grocery-account-tile tone-${tile.tone}`}
                    onClick={() => setIsPasswordModalOpen(true)}
                  >
                    {tileContent}
                  </button>
                ) : (
                  <Link className={`grocery-account-tile tone-${tile.tone}`} to={tile.to}>
                    {tileContent}
                  </Link>
                )}
              </Col>
            );
          })}
        </Row>

        <div className="grocery-account-signout">
          <Button danger ghost icon={<LogoutOutlined />} onClick={onSignOut}>
            {t('signOut', 'Sign out')}
          </Button>
        </div>

        <AppModal
          kind="account"
          title={t('changePassword', 'Change password')}
          open={isPasswordModalOpen}
          onCancel={closePasswordModal}
          footer={null}
          centered
          destroyOnHidden
          width={520}
          className="grocery-account-modal"
        >
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={onPasswordFinish}
            requiredMark={false}
          >
            <Form.Item
              label={t('currentPassword', 'Current password')}
              name="currentPassword"
              rules={[
                {
                  required: true,
                  message: t('currentPasswordRequired', 'Current password is required'),
                },
              ]}
            >
              <Input.Password size="large" autoComplete="current-password" />
            </Form.Item>
            <Form.Item
              label={t('newPassword', 'New password')}
              name="newPassword"
              rules={[
                {
                  required: true,
                  message: t('newPasswordRequired', 'New password is required'),
                },
              ]}
            >
              <Input.Password size="large" autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              label={t('confirmNewPassword', 'Confirm new password')}
              name="confirmPassword"
              rules={[
                {
                  required: true,
                  message: t('confirmPasswordRequired', 'Confirm password is required'),
                },
              ]}
            >
              <Input.Password size="large" autoComplete="new-password" />
            </Form.Item>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
              <Button onClick={closePasswordModal}>{t('cancel', 'Cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={changePassword.isPending}>
                {t('changePassword', 'Change password')}
              </Button>
            </div>
          </Form>
        </AppModal>

        <AppModal
          kind="account"
          title={
            isUploadMode
              ? t('editProfileImage', 'Edit profile photo')
              : t('editProfile', 'Edit profile')
          }
          open={isEditModalOpen}
          onCancel={closeEditModal}
          footer={null}
          centered
          destroyOnHidden
          width={520}
          className="grocery-account-modal"
        >
          {isUploadMode ? (
            <div>
              {selectedImagePreview ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: 24,
                    width: '100%',
                  }}
                >
                  <img
                    src={selectedImagePreview}
                    alt={t('selectedProfileImage', 'Selected profile image')}
                    style={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '1px solid #e6e6e6',
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    marginBottom: 24,
                    color: '#6b7280',
                    width: '100%',
                  }}
                >
                  {t('chooseProfileImage', 'Choose a profile image to upload')}
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: 24,
                  width: '100%',
                }}
              >
                <Button type="dashed" icon={<UploadOutlined />} size="large" onClick={openUpload}>
                  {t('uploadImage', 'Upload image')}
                </Button>
              </div>

              <input
                ref={uploadInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={onImageSelected}
              />

              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
                <Button onClick={closeEditModal}>{t('cancel', 'Cancel')}</Button>
                <Button
                  type="primary"
                  onClick={onUploadSubmit}
                  loading={update.isPending}
                  disabled={!selectedImageFile}
                >
                  {t('saveChanges', 'Save changes')}
                </Button>
              </div>
            </div>
          ) : (
            <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
              <Form.Item
                label={
                  <span>
                    {t('fullName', 'Full name')}
                    <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>
                  </span>
                }
                name="full_name"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t('fullNameRequired', 'Full name is required'),
                  },
                ]}
              >
                <Input
                  size="large"
                  maxLength={60}
                  autoComplete="name"
                  placeholder={t('enterFullName', 'Enter full name')}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span>
                    {t('email', 'Email')}
                    <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>
                  </span>
                }
                name="email"
                rules={[
                  { required: true, message: t('emailRequired', 'Email is required') },
                  { type: 'email', message: t('invalidEmail', 'Enter valid email') },
                ]}
              >
                <Input
                  type="email"
                  size="large"
                  maxLength={100}
                  placeholder={t('enterEmail', 'Enter email')}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span>
                    {t('phone', 'Phone')}
                    <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>
                  </span>
                }
                name="mobile"
                rules={[{ validator: (_, val) => validateMobileNumber(val, t) }]}
              >
                <MobileNumberField placeholder={t('enterMobileNumber', 'Enter mobile number')} />
              </Form.Item>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 24,
                }}
              >
                <Button type="primary" htmlType="submit" size="large" loading={update.isPending}>
                  {t('saveChanges', 'Save changes')}
                </Button>
              </div>
            </Form>
          )}
        </AppModal>
      </div>
    </Container>
  );
}
